import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatSAR, formatDate, statusColor } from "@/lib/utils";
import { toast } from "sonner";
import TripMap from "@/components/TripMap";
import { CalendarCheck, Wallet, ClipboardList, MapPin, ArrowRight } from "lucide-react";

function StatCard({ label, value, icon: Icon, color = "emerald" }) {
  const colors = { emerald: "bg-emerald-50 text-emerald-700", sky: "bg-sky-50 text-sky-700", amber: "bg-amber-50 text-amber-700", violet: "bg-violet-50 text-violet-700" };
  return (
    <div className="abdan-card p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
          <div className="text-2xl font-bold mt-1">{value}</div>
        </div>
        <div className={`w-12 h-12 rounded-xl grid place-items-center ${colors[color]}`}><Icon className="w-5 h-5" /></div>
      </div>
    </div>
  );
}

export function CustomerDashboard() {
  const { user, t } = useApp();
  const [bookings, setBookings] = useState([]);
  useEffect(() => { api.get("/bookings").then((r) => setBookings(r.data.items)); }, []);
  const active = bookings.filter((b) => !["completed", "cancelled"].includes(b.status)).length;
  const paid = bookings.filter((b) => b.payment_status === "paid").length;
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Assalamu'alaikum, {user.name}</h1>
        <p className="text-slate-500">Kelola perjalanan Anda dari satu tempat.</p>
      </div>
      <div className="grid md:grid-cols-4 gap-4">
        <StatCard label="Total Bookings" value={bookings.length} icon={ClipboardList} color="emerald" />
        <StatCard label="Active Trips" value={active} icon={MapPin} color="sky" />
        <StatCard label="Paid" value={paid} icon={Wallet} color="violet" />
        <StatCard label="Pending" value={bookings.filter((b) => b.status === "pending").length} icon={CalendarCheck} color="amber" />
      </div>
      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle>Recent Bookings</CardTitle>
          <Button onClick={() => (window.location.href = "/booking")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-new-booking">
            {t("book_now")} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </CardHeader>
        <CardContent><BookingTable rows={bookings.slice(0, 6)} showPay /></CardContent>
      </Card>
    </div>
  );
}

export function BookingTable({ rows, showPay = false, showAssign = false, refresh }) {
  const nav = useNavigate();
  const pay = async (b) => {
    try {
      const { data } = await api.post("/payments/checkout", { booking_id: b.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch { toast.error("Payment initialization failed"); }
  };
  return (
    <Table>
      <TableHeader>
        <TableRow><TableHead>Code</TableHead><TableHead>Route</TableHead><TableHead>Date</TableHead>
          <TableHead>Status</TableHead><TableHead>Payment</TableHead><TableHead>Price</TableHead><TableHead>Actions</TableHead></TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">No bookings yet.</TableCell></TableRow>}
        {rows.map((b) => (
          <TableRow key={b.id} data-testid={`row-booking-${b.code}`}>
            <TableCell className="font-mono font-semibold">{b.code}</TableCell>
            <TableCell><div className="font-medium">{b.origin} → {b.destination}</div><div className="text-xs text-slate-500">{b.vehicle_type}</div></TableCell>
            <TableCell className="text-sm">{formatDate(b.pickup_date)}</TableCell>
            <TableCell><Badge className={statusColor(b.status)}>{b.status}</Badge></TableCell>
            <TableCell><Badge className={statusColor(b.payment_status)}>{b.payment_status}</Badge></TableCell>
            <TableCell className="font-semibold">{formatSAR(b.price)}</TableCell>
            <TableCell>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => nav(`/customer/bookings/${b.id}`)} data-testid={`view-${b.code}`}>View</Button>
                {showPay && b.payment_status !== "paid" && (
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => pay(b)} data-testid={`pay-${b.code}`}>Pay</Button>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function CustomerBookings() {
  const [bookings, setBookings] = useState([]);
  const load = () => api.get("/bookings").then((r) => setBookings(r.data.items));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-bold">My Bookings</h1>
      <Card><CardContent className="p-4"><BookingTable rows={bookings} showPay refresh={load} /></CardContent></Card>
    </div>
  );
}

export function BookingDetail() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get(`/bookings/${id}`).then((r) => setB(r.data.booking)); }, [id]);
  if (!b) return <div>Loading...</div>;
  const pay = async () => {
    try {
      const { data } = await api.post("/payments/checkout", { booking_id: b.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch { toast.error("Payment failed"); }
  };
  return (
    <div className="space-y-6">
      <Button variant="outline" onClick={() => nav(-1)}>← Back</Button>
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle>Booking {b.code}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid md:grid-cols-2 gap-4">
              <Info label="Route" value={`${b.origin} → ${b.destination}`} />
              <Info label="Pickup" value={formatDate(b.pickup_date)} />
              <Info label="Vehicle" value={b.vehicle_type} />
              <Info label="Passengers" value={b.passengers} />
              <Info label="Status" value={<Badge className={statusColor(b.status)}>{b.status}</Badge>} />
              <Info label="Payment" value={<Badge className={statusColor(b.payment_status)}>{b.payment_status}</Badge>} />
            </div>
            <TripMap origin={b.origin} destination={b.destination} current={b.current_location} height={360} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Summary</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="text-3xl font-bold text-emerald-700">{formatSAR(b.price)}</div>
            {b.payment_status !== "paid" && <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={pay} data-testid="detail-pay">Pay Now</Button>}
            {b.payment_status === "paid" && <Badge className={statusColor("paid")}>Paid ✓</Badge>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return <div><div className="text-xs uppercase text-slate-500 tracking-widest">{label}</div><div className="font-semibold mt-1">{value}</div></div>;
}

export function LiveTracking() {
  const [bookings, setBookings] = useState([]);
  const [sel, setSel] = useState(null);
  useEffect(() => { api.get("/bookings").then((r) => { setBookings(r.data.items); setSel(r.data.items.find((b) => ["dispatched", "in-progress"].includes(b.status)) || r.data.items[0]); }); }, []);
  if (!sel) return <div className="text-slate-500">No active trips. <a href="/booking" className="text-emerald-600">Book one</a>.</div>;
  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-bold">Live Tracking</h1>
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardContent className="p-4"><TripMap origin={sel.origin} destination={sel.destination} current={sel.current_location} height={520} /></CardContent>
        </Card>
        <div className="space-y-2">
          {bookings.map((b) => (
            <button key={b.id} onClick={() => setSel(b)} data-testid={`track-${b.code}`}
              className={`w-full text-left abdan-card p-4 ${sel.id === b.id ? "ring-2 ring-emerald-500" : ""}`}>
              <div className="font-mono font-bold">{b.code}</div>
              <div className="text-sm text-slate-600">{b.origin} → {b.destination}</div>
              <Badge className={`mt-2 ${statusColor(b.status)}`}>{b.status}</Badge>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function PaymentSuccess() {
  const [sp] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const sid = sp.get("session_id");
  useEffect(() => {
    if (!sid) return;
    let cnt = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
      } catch {}
      if (++cnt < 15) setTimeout(poll, 2000); else setStatus("timeout");
    };
    poll();
  }, [sid]);
  return (
    <div className="max-w-lg mx-auto text-center py-16" data-testid="payment-success">
      <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center mx-auto mb-4">✓</div>
      <h1 className="text-3xl font-bold mb-2">Payment {status === "paid" ? "Confirmed" : "Processing"}</h1>
      <p className="text-slate-500 mb-6">{status === "paid" ? "Your booking is confirmed." : "We're verifying with Stripe..."}</p>
      <Button onClick={() => (window.location.href = "/customer/bookings")} className="bg-emerald-600 hover:bg-emerald-700">Back to Bookings</Button>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-lg mx-auto text-center py-16">
      <h1 className="text-3xl font-bold mb-2">Payment Cancelled</h1>
      <Button onClick={() => (window.location.href = "/customer/bookings")}>Back to Bookings</Button>
    </div>
  );
}

export function CustomerProfile() {
  const { user } = useApp();
  return (
    <div className="max-w-2xl">
      <h1 className="text-3xl font-bold mb-6">Profile</h1>
      <Card><CardContent className="p-6 space-y-3">
        <Info label="Name" value={user.name} />
        <Info label="Email" value={user.email} />
        <Info label="Phone" value={user.phone || "-"} />
        <Info label="Member Since" value={formatDate(user.created_at)} />
      </CardContent></Card>
    </div>
  );
}
